// Source: https://usehooks-ts.com/react-hook/use-event-listener.
import { useCallback, useEffect, useRef } from 'react';
import useIsomorphicLayoutEffect from './use-isomorphic-layout-effect.js';
/**
 * Custom hook that handles timeouts in React components using the setTimeout API.
 */
function useTimeout(callback, delay, options = {}) {
    const { scheduled = false } = options;
    const timeoutRef = useRef();
    const callbackRef = useRef(callback);
    // Remember the latest callback if it changes.
    useIsomorphicLayoutEffect(() => {
        callbackRef.current = callback;
    }, [callback]);
    const create = useCallback(() => {
        // Don't create if no delay.
        // Note: 0 is a valid value for delay.
        if (!delay && delay !== 0) {
            return;
        }
        const id = setTimeout(() => {
            callbackRef.current();
        }, delay);
        timeoutRef.current = id;
    }, [delay]);
    const cancel = useCallback(() => {
        const timeout = timeoutRef.current;
        if (!timeout) {
            return;
        }
        clearTimeout(timeout);
    }, []);
    useEffect(() => {
        // Don't schedule if specified.
        if (!scheduled) {
            // Make sure cleanup is done anyway.
            return cancel;
        }
        create();
        return cancel;
    }, [cancel, create, scheduled]);
    return scheduled ? [cancel] : [cancel, create];
}
export default useTimeout;
